export type { ScanMode, ScanFormat, ScanResult } from "./decoders/types";
import type { ScanFormat, ScanResult } from "./decoders/types";

/**
 * A decoder used by components. Production decoders hand back `ScanResult`
 * (text + symbology); the fixture seam may return a bare string, which the
 * hook normalizes to `{ text, format: "unknown" }`.
 */
export type CodeDecoder = (video: HTMLVideoElement) => Promise<string | ScanResult | null>;

/**
 * Test seam: a decoder that resolves to a fixed value with no camera. Used by
 * unit tests and, via `window.__REGALEGAL_FIXTURE_DECODER__`, by e2e. Pass a
 * format when the test depends on the symbology (scanner-surface 06).
 */
export function fixtureDecoder(uuid: string, format?: ScanFormat): CodeDecoder {
  return async () => (format ? { text: uuid, format } : uuid);
}

interface FixtureGlobal {
  __REGALEGAL_FIXTURE_DECODER__?: string;
}

/**
 * Decoder to use for a component: explicit prop wins, then the e2e fixture.
 *
 * When neither is set, returns a no-op so the caller can still shape-check;
 * production callers obtain the mode-specific backend from `decoders/create`.
 */
export function resolveDecoder(decoder?: CodeDecoder): CodeDecoder {
  if (decoder) return decoder;
  const fixture = (globalThis as FixtureGlobal).__REGALEGAL_FIXTURE_DECODER__;
  if (fixture) return fixtureDecoder(fixture);
  return async () => null;
}

/** Whether the current environment emulates scanning with the fixture hook. */
export function isFixtureMode(): boolean {
  return Boolean((globalThis as FixtureGlobal).__REGALEGAL_FIXTURE_DECODER__);
}
